import { TransactionRequest, TransactionResponse } from "ethers";

import { extendError } from "lib/errors";
import { additionalTxnErrorValidation } from "lib/errors/additionalValidation";
import { estimateGasLimit } from "lib/gas/estimateGasLimit";
import { GasPriceData, getGasPrice } from "lib/gas/gasPrice";
import { getProvider } from "lib/rpc";
import { getTenderlyConfig, simulateCallDataWithTenderly } from "lib/tenderly";
import { WalletSigner } from "lib/wallets";

import { TransactionWaiterResult, TxnCallback, TxnEventBuilder } from "./types";

export type WalletTxnCtx = {};

export type WalletTxnResult = {
  transactionHash: string;
  wait: () => Promise<TransactionWaiterResult>;
};

const REPLACEMENT_GAS_BUMP_NUM = 3n;
const REPLACEMENT_GAS_BUMP_DEN = 2n;
const MIN_GAS_BUMP_WEI = 1_000_000_000n;

function isUnderpricedError(error: unknown) {
  const message = `${(error as any)?.message ?? (error as any)?.error?.message ?? ""}`.toLowerCase();
  return message.includes("replacement transaction underpriced") || message.includes("underpriced");
}

function isExecutionRevertedError(error: unknown) {
  const message = `${(error as any)?.message ?? (error as any)?.error?.message ?? ""}`.toLowerCase();
  return message.includes("execution reverted") || message.includes("reverted");
}

function bumpGasValue(value: bigint) {
  const bumped = (value * REPLACEMENT_GAS_BUMP_NUM + REPLACEMENT_GAS_BUMP_DEN - 1n) / REPLACEMENT_GAS_BUMP_DEN;
  const minBump = value + MIN_GAS_BUMP_WEI;
  return bumped > minBump ? bumped : minBump;
}

function bumpGasPriceData(data: GasPriceData): GasPriceData {
  if ("gasPrice" in data) {
    return { gasPrice: bumpGasValue(data.gasPrice) };
  }

  return {
    maxFeePerGas: bumpGasValue(data.maxFeePerGas),
    maxPriorityFeePerGas: bumpGasValue(data.maxPriorityFeePerGas),
  };
}

export async function sendWalletTransaction({
  chainId,
  signer,
  to,
  callData,
  value,
  gasLimit,
  gasPriceData,
  runSimulation,
  nonce,
  autoClearNonceGap,
  nonceGapThreshold,
  msg,
  callback,
}: {
  chainId: number;
  signer: WalletSigner;
  to: string;
  callData: string;
  value?: bigint | number;
  gasLimit?: bigint | number;
  gasPriceData?: GasPriceData;
  nonce?: number | bigint;
  autoClearNonceGap?: boolean;
  nonceGapThreshold?: number;
  msg?: string;
  runSimulation?: () => Promise<void>;
  callback?: TxnCallback<WalletTxnCtx>;
}) {
  const from = signer.address;
  const eventBuilder = new TxnEventBuilder<WalletTxnCtx>({});

  try {
    let resolvedNonce = nonce !== undefined ? Number(nonce) : undefined;
    if (resolvedNonce === undefined && autoClearNonceGap && signer.provider) {
      const [latestNonce, pendingNonce] = await Promise.all([
        signer.provider.getTransactionCount(from, "latest"),
        signer.provider.getTransactionCount(from, "pending"),
      ]);
      const gapThreshold = nonceGapThreshold ?? 0;
      if (pendingNonce > latestNonce + gapThreshold) {
        // eslint-disable-next-line no-console
        console.warn(
          `Detected pending nonce gap for ${from}. latest=${latestNonce} pending=${pendingNonce}. Using latest nonce to replace stuck txs.`
        );
        resolvedNonce = latestNonce;
      }
    }

    const tenderlyConfig = getTenderlyConfig();

    if (tenderlyConfig) {
      await simulateCallDataWithTenderly({
        chainId,
        tenderlyConfig,
        provider: signer.provider!,
        to,
        data: callData,
        from,
        value: value,
        gasLimit: gasLimit,
        gasPriceData: gasPriceData,
        blockNumber: undefined,
        comment: msg,
      });
      return {
        transactionHash: undefined,
        wait: async () => ({
          transactionHash: undefined,
          blockNumber: undefined,
          status: "success",
        }),
      };
    }

    const gasLimitPromise = gasLimit
      ? Promise.resolve(gasLimit)
      : estimateGasLimit(signer.provider!, {
        to,
        from,
        data: callData,
        value,
      }).catch((error) => {
        if (isExecutionRevertedError(error)) {
          throw extendError(error, {
            errorContext: "simulation",
          });
        }

        return undefined;
      });

    const provider = getProvider(undefined, chainId);
    const gasPriceDataPromise = gasPriceData
      ? Promise.resolve(gasPriceData)
      : getGasPrice(provider, chainId).catch(() => undefined);

    const [gasLimitResult, gasPriceDataResult] = await Promise.all([
      gasLimitPromise,
      gasPriceDataPromise,
      runSimulation?.().then(() => callback?.(eventBuilder.Simulated())),
    ]);

    callback?.(eventBuilder.Sending());

    let gasDataToUse = gasPriceDataResult;

    for (let attempt = 0; attempt <= 2; attempt += 1) {
      const txnData: TransactionRequest = {
        to,
        data: callData,
        value,
        from,
        nonce: resolvedNonce,
        gasLimit: gasLimitResult,
        ...(gasDataToUse ?? {}),
      };

      try {
        const res = await signer.sendTransaction(txnData);

        callback?.(
          eventBuilder.Sent({
            type: "wallet",
            transactionHash: res.hash,
          })
        );

        return {
          transactionHash: res.hash,
          wait: makeWalletTxnResultWaiter(res.hash, res),
        };
      } catch (error) {
        additionalTxnErrorValidation(error, chainId, signer.provider!, txnData);

        if (isUnderpricedError(error) && attempt < 2) {
          if (gasDataToUse) {
            gasDataToUse = bumpGasPriceData(gasDataToUse);
          } else {
            const fallbackGasData = await getGasPrice(provider, chainId).catch(() => undefined);
            if (fallbackGasData) {
              gasDataToUse = bumpGasPriceData(fallbackGasData);
            }
          }
          continue;
        }

        throw extendError(error, {
          errorContext: "sending",
        });
      }
    }

    throw new Error("Failed to send transaction after retrying underpriced replacement transaction");
  } catch (error) {
    callback?.(eventBuilder.Error(error));

    throw error;
  }
}

function makeWalletTxnResultWaiter(hash: string, txn: TransactionResponse) {
  return async () => {
    const receipt = await txn.wait();
    return {
      transactionHash: hash,
      blockNumber: receipt?.blockNumber,
      status: receipt?.status === 1 ? "success" : "failed",
    };
  };
}
